<script setup lang="ts">
  import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
  } from '@/shared/ui';
  import { List } from 'lucide-vue-next';
  import { computed } from 'vue';
  import { useModal } from 'vue-final-modal';
  import * as z from 'zod';
  import type {
    AlternativeItem,
    DropdownProperties as DropdownPropsType,
  } from '../../../model/form.types';
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import { FORM_FIELD_CHARACTER_LIMITS } from '../../../lib/form-field-character-limits';
  import DropdownAlternativesModal from './DropdownAlternativesModal.vue';

  interface Props {
    elementId?: string;
  }

  const props = defineProps<Props>();

  const { selectedElement } = usePropertyForm<
    {
      name: string;
      label?: string;
      placeholder?: string;
      required?: boolean;
      labelPosition?: 'top' | 'left';
      alternatives: AlternativeItem[];
      multipleSelection?: boolean;
    },
    DropdownPropsType
  >({
    elementId: () => props.elementId,
    validationSchema: z.object({
      name: z.string().min(1, 'O nome é obrigatório').max(FORM_FIELD_CHARACTER_LIMITS.FIELD_ID),
      label: z.string().max(FORM_FIELD_CHARACTER_LIMITS.LABEL).optional(),
      placeholder: z.preprocess(
        (val) => (val === '' ? undefined : val),
        z.string().max(FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT).optional()
      ),
      required: z.boolean().optional(),
      labelPosition: z.enum(['top', 'left']).optional(),
      alternatives: z.array(
        z.object({
          id: z.string(),
          value: z.string(),
        })
      ),
      multipleSelection: z.boolean().optional(),
    }),
    extractInitialValues: (
      properties: DropdownPropsType | undefined
    ): {
      name: string;
      label?: string;
      placeholder?: string;
      required?: boolean;
      labelPosition?: 'top' | 'left';
      alternatives: AlternativeItem[];
      multipleSelection?: boolean;
    } => ({
      name: properties?.name ?? '',
      label: properties?.label ?? '',
      placeholder: properties?.placeholder ?? '',
      required: properties?.required ?? false,
      labelPosition: properties?.labelPosition ?? 'top',
      alternatives: properties?.alternatives ?? [{ id: 'alt-default', value: 'Opção 1' }],
      multipleSelection: properties?.multipleSelection ?? false,
    }),
    mapValuesToProperties: (
      values: {
        name: string;
        label?: string;
        placeholder?: string;
        required?: boolean;
        labelPosition?: 'top' | 'left';
        alternatives: AlternativeItem[];
        multipleSelection?: boolean;
      },
      currentProperties: DropdownPropsType
    ) => {
      const formValues = { ...values };
      delete (formValues as { alternatives?: AlternativeItem[] }).alternatives;
      return {
        properties: {
          ...currentProperties,
          ...formValues,
          name: values.name || currentProperties.name,
          placeholder: values.placeholder || undefined,
        },
      };
    },
  });

  const alternativesCount = computed(
    () => (selectedElement.value?.properties as DropdownPropsType)?.alternatives?.length ?? 0
  );

  const alternativesModal = useModal({
    component: DropdownAlternativesModal,
    attrs: {
      elementId: props.elementId ?? '',
      onClose() {
        alternativesModal.close();
      },
    },
  });
</script>

<template>
  <div class="flex flex-col">
    <!-- ID do campo -->
    <div class="border-b border-[var(--color-gray-02)] py-4">
      <FormField
        v-slot="{ componentField }"
        name="name"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-[var(--color-gray-07)] font-normal">
            ID do campo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="nome_do_campo"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.FIELD_ID"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Texto auxiliar -->
    <div class="border-b border-[var(--color-gray-02)] py-4">
      <FormField
        v-slot="{ componentField }"
        name="placeholder"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-[var(--color-gray-07)] font-normal">
            Texto auxiliar
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui o texto auxiliar"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Rótulo + Posicionamento -->
    <div class="border-b border-[var(--color-gray-02)] py-4 flex flex-col gap-[10px]">
      <FormField
        v-slot="{ componentField }"
        name="label"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-[var(--color-gray-07)] font-normal">
            Rótulo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui"
              class="h-[42px]"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.LABEL"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ componentField }"
        name="labelPosition"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-[var(--color-gray-07)] font-normal">
            Posicionamento
          </FormLabel>
          <Select v-bind="componentField">
            <FormControl>
              <SelectTrigger class="h-11 w-full">
                <SelectValue placeholder="Selecione a posição" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="top">
                Acima do campo
              </SelectItem>
              <SelectItem value="left">
                À Esquerda do Campo
              </SelectItem>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Obrigatória -->
    <div class="border-b border-[var(--color-gray-02)] py-4">
      <FormField
        v-slot="{ componentField }"
        name="required"
      >
        <FormItem class="flex items-center justify-between h-5">
          <FormLabel class="text-sm text-[var(--color-gray-07)] font-normal cursor-pointer">
            Obrigatória
          </FormLabel>
          <FormControl>
            <Switch v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>
    </div>

    <!-- Alternativas header -->
    <div class="flex gap-2 items-center h-[42px] py-2 border-b border-gray-03 mt-4">
      <div class="bg-gray-03 flex items-center justify-center p-0.5 rounded shrink-0 size-6">
        <List
          :size="18"
          class="text-white-01"
        />
      </div>
      <span class="text-sm text-[var(--color-gray-07)]">Alternativas</span>
    </div>

    <!-- Alternativas content -->
    <div class="border-b border-[var(--color-gray-02)] py-4 flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <button
          type="button"
          class="w-full h-[42px] bg-[var(--theme-secondary)] text-white text-sm font-medium rounded-lg flex items-center justify-center shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
          @click="alternativesModal.open()"
        >
          Editar alternativas
        </button>
        <p class="text-[10px]">
          <span class="text-[var(--color-gray-07)]">Total de alternativas: </span>
          <span class="text-[var(--color-gray-03)]">{{ alternativesCount }}</span>
        </p>
      </div>

      <FormField
        v-slot="{ componentField }"
        name="multipleSelection"
      >
        <FormItem class="flex items-center justify-between h-5">
          <FormLabel class="text-sm text-[var(--color-gray-07)] font-normal cursor-pointer">
            Seleção múltipla
          </FormLabel>
          <FormControl>
            <Switch v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>
    </div>
  </div>
</template>
